import { useState, type ReactElement } from "react";

export interface RuleHealthView {
  readonly sport: string;
  readonly rule: string;
  readonly name: string;
  readonly tracked: boolean;
  readonly bets: number;
  readonly roi: number;
  readonly band: number;
  readonly t: number;
  readonly backtestRoi: number | null;
  readonly behindBacktest: boolean;
  readonly gate: "PASSED" | "FAILED" | "NONE";
  readonly verdict: "PASS" | "HOLD" | "BIN";
  readonly checks: {
    readonly bets: boolean;
    readonly t: boolean;
    readonly seasons: boolean;
    readonly halves: boolean;
    readonly worsePrice: boolean;
  };
  readonly toPass: number;
  readonly toBin: number;
}

const pct = (value: number) =>
  `${value >= 0 ? "+" : "−"}${Math.abs(value * 100).toFixed(1)}%`;

const VERDICT_ORDER = { PASS: 0, BIN: 1, HOLD: 2 } as const;

const CHECKS: readonly [keyof RuleHealthView["checks"], string][] = [
  ["bets", "300 bets"],
  ["t", "t-stat"],
  ["seasons", "Every season up"],
  ["halves", "Both halves up"],
  ["worsePrice", "Up at −0.05 price"],
];

/**
 * Rule health (Moss, 2 Nov 2026): every rule against its own backtest and the
 * review standard (docs/rule-intake/REVIEW_STANDARD.md). The verdict is a
 * prompt for the monthly review; nothing changes a stake by itself.
 */
export function RulesPage({
  rows = [],
}: {
  readonly rows?: readonly RuleHealthView[];
}): ReactElement {
  const [scope, setScope] = useState<"STAKED" | "TRACKED">("STAKED");
  const shown = rows
    .filter((row) => row.tracked === (scope === "TRACKED"))
    .toSorted(
      (a, b) =>
        VERDICT_ORDER[a.verdict] - VERDICT_ORDER[b.verdict] || b.bets - a.bets,
    );
  return (
    <div className="rules-page">
      <header className="page-heading">
        <h2>Rule health</h2>
        <p>
          PASS needs 300 bets, t ≥ 2.5 (3.0 without a history test), every
          season and both halves up, and still up at a price 0.05 worse. BIN
          once 150 bets can't reach +3% even at the top of the band.
        </p>
      </header>
      <div className="sport-selector" role="group" aria-label="Rule scope">
        {(["STAKED", "TRACKED"] as const).map((item) => (
          <button
            key={item}
            type="button"
            aria-pressed={scope === item}
            onClick={() => setScope(item)}
          >
            {item === "STAKED" ? "Staked" : "Tracked $0"} (
            {rows.filter((row) => row.tracked === (item === "TRACKED")).length})
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <p className="tile-empty">No graded bets yet.</p>
      ) : (
        <div className="rule-health-grid">
          {shown.map((row) => (
            <article
              className={`card rule-health verdict-${row.verdict.toLowerCase()}`}
              key={`${row.sport}|${row.rule}|${String(row.tracked)}`}
            >
              <header>
                <div>
                  <span className="eyebrow">
                    {row.sport} · {row.bets} bets
                  </span>
                  <h3>{row.name}</h3>
                </div>
                <span className="rule-verdict">{row.verdict}</span>
              </header>
              <div className="rule-health-numbers">
                <div>
                  <span>ROI</span>
                  <strong className={row.roi >= 0 ? "positive" : "negative"}>
                    {pct(row.roi)}
                  </strong>
                  <small>
                    ± {(row.band * 100).toFixed(1)}% · t {row.t.toFixed(2)}
                  </small>
                </div>
                <div>
                  <span>Backtest</span>
                  <strong>
                    {row.backtestRoi === null ? "–" : pct(row.backtestRoi)}
                  </strong>
                  <small>
                    {row.backtestRoi === null
                      ? row.gate === "FAILED"
                        ? "Failed its entry gate"
                        : "No history test"
                      : row.behindBacktest
                        ? "Running behind it"
                        : "Within range"}
                  </small>
                </div>
              </div>
              <div
                className="rule-progress"
                role="progressbar"
                aria-label="Bets towards a verdict"
                aria-valuemin={0}
                aria-valuemax={300}
                aria-valuenow={Math.min(row.bets, 300)}
              >
                <span style={{ width: `${Math.min(100, row.bets / 3)}%` }} />
              </div>
              <small className="rule-progress-label">
                {row.toPass > 0
                  ? `${String(row.toPass)} more bets before PASS can be judged${row.toBin > 0 ? `, ${String(row.toBin)} before BIN` : ""}`
                  : "Enough bets to judge"}
              </small>
              <ul className="rule-checks">
                {CHECKS.map(([key, label]) => (
                  <li key={key} className={row.checks[key] ? "met" : "unmet"}>
                    {row.checks[key] ? "✓" : "·"} {label}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
